import React, { useState, useEffect, useMemo } from "react";
import { Box, Stack } from "@mui/material";
import { useNavigate } from "react-router";
import Topbar from "../../../../../../../component/TopBar";
import MainBox from "../../../../../container/MainBox";
import BtnPrimary from "../../../../../../../component/PrimaryButton";
import DataTable from "../../../../../../../component/DataTable";
import { SearchInput, StatCard, StatusChip } from "../../../../../../../component/listing";
import { formatDate, formatDateByDay } from "../../../../../../../utils/formatDate";
import { getAllShiftRotations } from "../../../../../../../services/shiftRotation";
import { useNotify } from "../../../../../../../component/NotificationProvider";
import { tokens } from "../../../../../../../theme";

function ShiftRotationListing() {
  const navigate = useNavigate();
  const notify = useNotify();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [search, setSearch] = useState("");

  const fetchData = async () => {
    setLoading(true);
    setError(false);
    try {
      const res = await getAllShiftRotations();
      if (res?.success === false) throw new Error(res?.message || "Failed to load shift rotations");
      setData(Array.isArray(res?.data) ? res.data : []);
    } catch (err) {
      setError(true);
      notify.error(err?.message || "Failed to load shift rotations");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return data;
    return data.filter((r) => (r.name || "").toLowerCase().includes(q));
  }, [data, search]);

  const stats = useMemo(() => ({
    total: data.length,
    active: data.filter((r) => r.status !== "inactive").length,
  }), [data]);

  const openEdit = (id) => navigate("/app/shift-rotation/edit", { state: { rotationId: id } });

  return (
    <Box>
      <Topbar MainHeading={"Shift Rotation"} />

      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: "1.5rem" }}>
        <StatCard label="Total rotations" value={stats.total} />
        <StatCard label="Active" value={stats.active} accent={tokens.success} />
      </Stack>

      <MainBox sx={{ padding: "1.25rem" }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} justifyContent="space-between" alignItems={{ md: "center" }} sx={{ mb: "1.25rem" }}>
          <SearchInput value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search rotation name" />
          <BtnPrimary to="/app/shift-rotation/new/rotation-details" sx={{ borderRadius: "8px", whiteSpace: "nowrap" }}>
            + Add Rotation
          </BtnPrimary>
        </Stack>

        <DataTable
          rows={filtered}
          loading={loading}
          error={error}
          onRetry={fetchData}
          errorTitle="Couldn't load rotations"
          empty={{
            title: data.length === 0 ? "No shift rotations yet" : "No matches",
            description:
              data.length === 0
                ? "Create a rotation to cycle employees through shifts on a schedule."
                : "Try a different search.",
          }}
          onRowClick={(r) => openEdit(r._id)}
          tableSx={{ width: "100%", tableLayout: "fixed" }}
          columns={[
            {
              key: "name",
              label: "Rotation Name",
              cellSx: { fontWeight: 600, color: tokens.accent },
              render: (r) => r.name || "—",
            },
            { key: "shifts", label: "Shifts", render: (r) => r?.shifts?.length || 0 },
            {
              key: "frequency",
              label: "Frequency",
              cellSx: { color: tokens.textMuted },
              render: (r) =>
                r.rotationFrequency
                  ? `Every ${r.rotationFrequency} ${r.rotationType === "Monthly" ? "month(s)" : "week(s)"}`
                  : "—",
            },
            {
              key: "startDate",
              label: "Start Date",
              cellSx: { color: tokens.textMuted },
              render: (r) => (r.startDate ? formatDate(r.startDate) : "—"),
            },
            {
              key: "status",
              label: "Status",
              render: (r) => (
                <StatusChip
                  label={r.status === "inactive" ? "Inactive" : "Active"}
                  tone={r.status === "inactive" ? "muted" : "success"}
                />
              ),
            },
            {
              key: "createdAt",
              label: "Created On",
              cellSx: { color: tokens.textMuted },
              render: (r) => formatDateByDay(r.createdAt),
            },
          ]}
        />

      </MainBox>
    </Box>
  );
}

export default ShiftRotationListing;
